/**
 * 降级与告警（Alice 版式）：模型级降级统计、降级事件流水，以及渠道自动停用、恢复、全链失败等运维告警。
 *
 * 背景：降级链会在上游抖动时悄悄切换渠道，请求本身看起来成功；运营需要看到切换了多少次、哪个模型整条链都失败，
 *   与 Alice 的降级监控口径一致（切换与全链失败两类事件，窗口内统计）。
 * 设计意图：功能与原页相同（统计窗口、事件类型筛选、告警列表），只把外观换成 Alice 的指标卡加小节卡；
 *   原来的三个分页签改成同页三个小节，运营不必来回切换。告警来自数据面写入 Redis 的最近列表，不额外落库。
 * 关键约束：只读页面，支持与客服角色共用；窗口与条数在接口侧有上限，页面不做自动轮询。
 */
import { useState } from 'react'
import { adminApi } from '@ap/api-client/admin'
import { A } from '@ap/i18n/admin'
import { AG_runtime2 as G } from '@ap/i18n/admin-runtime2'
import { C, errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, useLoad } from '@ap/ui'
import { AliceBadge, OpsPage, Stat, Stats, TableWrap, Toolbar } from '../alice/kit.tsx'
import { EmptyRow, Section, ToolSelect, ellipsis } from '../alice/kit-runtime2.tsx'

export function Fallback() {
  const { t, locale } = useI18n()
  const [minutes, setMinutes] = useState('30')
  const [kind, setKind] = useState('')
  const stats = useLoad(() => adminApi.fallbackStats(Number(minutes)), [minutes])
  const events = useLoad(() => adminApi.fallbackEvents({ kind: kind || undefined, limit: 100 }), [kind])
  const alerts = useLoad(() => adminApi.alerts(100), [])
  const reload = () => {
    stats.reload()
    events.reload()
    alerts.reload()
  }

  const statRows = stats.data?.data ?? []
  const switches = statRows.reduce((n, r) => n + r.switches, 0)
  const allFailed = statRows.reduce((n, r) => n + r.all_failed, 0)
  const requests = statRows.reduce((n, r) => n + r.total, 0)
  const alertRows = alerts.data?.data ?? []
  const critical = alertRows.filter((a) => a.severity === 'critical').length
  const warning = alertRows.filter((a) => a.severity === 'warning').length
  const dash = '-'
  const rate = (a: number, total: number) => `${total ? ((a / total) * 100).toFixed(2) : '0.00'}%`
  const msg = (e: unknown) => errorText(e, locale)

  return (
    <OpsPage eyebrow={t(G.fallback.eyebrow)} title={t(A.fb.title)} desc={t(A.fb.lead)} chip={t(G.fallback.chip)}>
      <Toolbar>
        <ToolSelect value={minutes} onChange={(e) => setMinutes(e.target.value)} options={['30', '60', '360', '1440'].map((m) => ({ value: m, label: t(A.fb.window, { n: m }) }))} />
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          {t(C.refresh)}
        </button>
      </Toolbar>

      <Stats>
        <Stat label={t(G.fallback.statSwitches)} value={stats.data ? switches.toLocaleString() : dash} sub={t(G.fallback.statSwitchesSub)} />
        <Stat label={t(G.fallback.statAllFailed)} value={stats.data ? allFailed.toLocaleString() : dash} color={allFailed > 0 ? 'error' : undefined} sub={t(G.fallback.statAllFailedSub)} />
        <Stat label={t(G.fallback.statRate)} value={stats.data ? rate(allFailed, requests) : dash} sub={t(G.fallback.statRateSub)} />
        <Stat label={t(G.fallback.statAlerts)} value={alerts.data ? alertRows.length.toLocaleString() : dash} sub={t(G.fallback.statAlertsSub, { c: critical, w: warning })} />
      </Stats>

      <Section title={t(G.fallback.statsTitle)} copy={t(G.fallback.statsCopy)}>
        <TableWrap
          head={
            <>
              <th>{t(A.fb.model)}</th>
              <th>{t(A.fb.requests)}</th>
              <th>{t(A.fb.switches)}</th>
              <th>{t(A.fb.allFailed)}</th>
              <th>{t(A.fb.rate)}</th>
            </>
          }
        >
          {stats.error ? (
            <EmptyRow cols={5}>{msg(stats.error)}</EmptyRow>
          ) : stats.loading && !stats.data ? (
            <EmptyRow cols={5}>{t(G.common.loading)}</EmptyRow>
          ) : statRows.length === 0 ? (
            <EmptyRow cols={5}>{t(G.fallback.empty)}</EmptyRow>
          ) : (
            statRows.map((r) => (
              <tr key={r.model}>
                <td className="mono">{r.model}</td>
                <td>{r.total.toLocaleString()}</td>
                <td>{r.switches.toLocaleString()}</td>
                <td>{r.all_failed > 0 ? <AliceBadge tone="off">{r.all_failed}</AliceBadge> : 0}</td>
                <td>{rate(r.all_failed, r.total)}</td>
              </tr>
            ))
          )}
        </TableWrap>
      </Section>

      <Section title={t(G.fallback.eventsTitle)} copy={t(G.fallback.eventsCopy)}>
        <div style={{ padding: '14px 20px 0' }}>
          <ToolSelect
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={[
              { value: '', label: t(A.fb.allKinds) },
              { value: 'switch', label: t(A.fb.kindSwitch) },
              { value: 'all_failed', label: t(A.fb.kindAllFailed) },
            ]}
          />
        </div>
        <TableWrap
          head={
            <>
              <th>{t(A.fb.time)}</th>
              <th>{t(A.fb.kind)}</th>
              <th>{t(A.fb.model)}</th>
              <th>{t(A.fb.from)}</th>
              <th>{t(A.fb.to)}</th>
              <th>{t(A.fb.error)}</th>
            </>
          }
        >
          {events.error ? (
            <EmptyRow cols={6}>{msg(events.error)}</EmptyRow>
          ) : events.loading && !events.data ? (
            <EmptyRow cols={6}>{t(G.common.loading)}</EmptyRow>
          ) : (events.data?.data ?? []).length === 0 ? (
            <EmptyRow cols={6}>{t(G.fallback.empty)}</EmptyRow>
          ) : (
            (events.data?.data ?? []).map((e) => (
              <tr key={e.id}>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(e.created_at, locale)}</td>
                <td>
                  <AliceBadge tone={e.kind === 'all_failed' ? 'off' : 'info'}>{t(e.kind === 'all_failed' ? A.fb.kindAllFailed : A.fb.kindSwitch)}</AliceBadge>
                </td>
                <td className="mono">{e.model_id}</td>
                <td className="mono">{e.primary_channel}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {e.to_channel ? `${e.to_channel} / ${e.to_upstream_model}` : dash}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 320, ...ellipsis }} title={e.error_message}>
                  {e.error_message}
                </td>
              </tr>
            ))
          )}
        </TableWrap>
      </Section>

      <Section title={t(G.fallback.alertsTitle)} copy={t(G.fallback.alertsCopy)}>
        <TableWrap
          head={
            <>
              <th>{t(A.fb.time)}</th>
              <th>{t(A.fb.severity)}</th>
              <th>{t(A.fb.kind)}</th>
              <th>{t(G.fallback.alertsTitle)}</th>
            </>
          }
        >
          {alerts.error ? (
            <EmptyRow cols={4}>{msg(alerts.error)}</EmptyRow>
          ) : alerts.loading && !alerts.data ? (
            <EmptyRow cols={4}>{t(G.common.loading)}</EmptyRow>
          ) : alertRows.length === 0 ? (
            <EmptyRow cols={4}>{t(A.fb.noAlerts)}</EmptyRow>
          ) : (
            alertRows.map((a) => (
              <tr key={`${a.ts}-${a.kind}-${a.title}`}>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(new Date(a.ts * 1000).toISOString(), locale)}</td>
                <td>
                  <AliceBadge tone={a.severity === 'critical' ? 'off' : a.severity === 'warning' ? 'info' : 'ok'}>{a.severity}</AliceBadge>
                </td>
                <td className="mono">{a.kind}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{a.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{a.body}</div>
                </td>
              </tr>
            ))
          )}
        </TableWrap>
      </Section>
    </OpsPage>
  )
}
